import { Box, Link, Stack, Typography } from '@mui/material';
import type React from 'react';
import { memo, useMemo } from 'react';
import { ToastWithProgress } from '@/feature/notifications/components/toast_with_progress';
import { useNotificationStore } from '@/feature/notifications/notifications_store';
import type { PersistentNotification } from '@/feature/notifications/types';
import { useAnnouncementsStore } from '../announcements_store';

const ANNOUNCEMENT_WIDTH = 360;
const TOP_OFFSET = 64;
const BOTTOM_OFFSET = 24;
const RIGHT_OFFSET = 24;

// Matches http(s):// and bare www. URLs so plain-text announcement content can embed clickable links.
// `split` with the capture group yields alternating [text, url, text, url, ...] segments.
const URL_SPLIT_REGEX = /((?:https?:\/\/|www\.)[^\s]+)/gi;
const URL_TEST_REGEX = /^(?:https?:\/\/|www\.)[^\s]+$/i;

/** Splits plain-text content into text + clickable-link segments. */
function renderContentWithLinks(content: string): React.ReactNode[] {
  return content.split(URL_SPLIT_REGEX).map((segment, index) => {
    if (!URL_TEST_REGEX.test(segment)) return segment;
    const href = /^www\./i.test(segment) ? `https://${segment}` : segment;
    return (
      <Link key={`${index}-${segment}`} href={href} target="_blank" rel="noopener noreferrer" sx={{ wordBreak: 'break-word' }}>
        {segment}
      </Link>
    );
  });
}

// A client-injected announcement not backed by the API (e.g. a wallet-specific notice),
// rendered in the same stack as API ones so they never overlap. Caller owns dismissal.
export interface ExtraAnnouncement {
  id: string;
  title: string;
  content: string;
  onClose: () => void;
}

export const AnnouncementsContainer = memo(function AnnouncementsContainer({
  extraAnnouncements = [],
}: {
  extraAnnouncements?: ExtraAnnouncement[];
}) {
  const announcements = useAnnouncementsStore((state) => state.announcements);
  const readIds = useAnnouncementsStore((state) => state.readIds);
  const markRead = useAnnouncementsStore((state) => state.markRead);
  // Position announcements opposite to regular notifications to avoid overlap
  const isTop = useNotificationStore((state) => state.position === 'bottom-right');
  const announcementPosition = isTop ? 'top-right' : 'bottom-right';

  const items = useMemo<ExtraAnnouncement[]>(
    () => [
      ...announcements
        .filter((a) => !readIds.includes(a.id))
        .map((a) => ({ id: String(a.id), title: a.title, content: a.content, onClose: () => markRead(a.id) })),
      ...extraAnnouncements,
    ],
    [announcements, readIds, markRead, extraAnnouncements]
  );

  if (items.length === 0) return null;

  return (
    <Stack
      spacing={2}
      sx={{
        position: 'fixed',
        ...(isTop ? { top: TOP_OFFSET, bottom: 'auto' } : { bottom: BOTTOM_OFFSET, top: 'auto' }),
        right: RIGHT_OFFSET,
        zIndex: 1400,
        width: ANNOUNCEMENT_WIDTH,
      }}
    >
      {items.map(({ id, title, content, onClose }) => {
        const notification: PersistentNotification = {
          id,
          severity: 'announcement',
          message: (
            <Box>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                {title}
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', whiteSpace: 'pre-line' }}>
                {renderContentWithLinks(content)}
              </Typography>
            </Box>
          ),
          manualDismiss: true,
          read: false,
          created: Date.now(),
          superseded: false,
        };
        return (
          <ToastWithProgress
            key={id}
            notification={notification}
            onClose={onClose}
            autoHideDuration={0}
            position={announcementPosition}
          />
        );
      })}
    </Stack>
  );
});
